import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import config from "../../Config";
import axios from "axios";
import moment from "moment";
import { Form } from "react-bootstrap";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import StudentMenu from "./StudentMenu";
import RightsCheck from "../General/RightsCheck";
import { scrollToTop } from "../Shared/ScrollOnTop";

const StudentPaymentReport = () => {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const MemberType = localStorage.getItem("MemberType");

  const [searchRegNo, setSearchRegNo] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [dateFrom, setDateFrom] = useState(
    moment().subtract(1, "year").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));

  const [reportLoading, setReportLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [reportMeta, setReportMeta] = useState(null);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    document.title = "Student payment report";

    const urlParams = new URLSearchParams(window.location.search);
    const sid = parseInt(urlParams.get("Student_ID") || "0", 10);
    const reg = urlParams.get("Registration_No") || "";
    if (reg) setSearchRegNo(reg);
    if (sid > 0) {
      setSelectedStudent({
        Student_ID: sid,
        Registration_No: reg,
        Student_Name: urlParams.get("Student_Name") || "",
      });
      if (!reg) {
        axios({
          method: "post",
          url: config.base_url + "Student/SearchStudents",
          headers: { "Content-Type": "application/json" },
          data: {
            AccessKey,
            UserID,
            MemberType,
            pageNo: 1,
            pageSize: 1,
            StudentID: sid,
          },
        }).then((res) => {
          if (res.data?.status_code === 1 && res.data.ListofRecords?.[0]) {
            setSelectedStudent(res.data.ListofRecords[0]);
            setSearchRegNo(res.data.ListofRecords[0].Registration_No || "");
          }
        });
      }
    }
  }, []);

  const searchStudent = () => {
    const reg = searchRegNo.trim();
    if (!reg) {
      toast.error("Enter registration number.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setSearchLoading(true);
    setSelectedStudent(null);
    setPayments([]);
    setReportMeta(null);

    axios({
      method: "post",
      url: config.base_url + "Student/SearchStudents",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        pageNo: 1,
        pageSize: 5,
        Registration_No: reg,
        OrderBy: "Student_Name",
      },
    })
      .then((res) => {
        if (res.data?.status_code === 1 && res.data.ListofRecords?.length > 0) {
          const match =
            res.data.ListofRecords.find(
              (s) =>
                String(s.Registration_No || "").toLowerCase() ===
                reg.toLowerCase()
            ) || res.data.ListofRecords[0];
          setSelectedStudent(match);
        } else {
          toast.error("No student found for that registration number.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        setSearchLoading(false);
      })
      .catch(() => {
        setSearchLoading(false);
        toast.error("Network error searching student.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const loadReport = (event) => {
    event.preventDefault();
    setValidated(true);

    if (!selectedStudent?.Student_ID) {
      toast.error("Search and select a student first.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    if (!dateFrom || !dateTo) {
      toast.error("From and to dates are required.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setReportLoading(true);
    setPayments([]);
    setReportMeta(null);

    axios({
      method: "post",
      url: config.base_url + "Student/GetStudentPaymentReport",
      headers: { "Content-Type": "application/json" },
      data: {
        AccessKey,
        UserID,
        MemberType,
        Student_ID: selectedStudent.Student_ID,
        StartDate: dateFrom,
        EndDate: dateTo,
      },
    })
      .then((res) => {
        if (res.data?.status_code === 1) {
          setPayments(res.data.ListofPayments || []);
          setReportMeta(res.data);
        } else {
          toast.error(
            res.data?.status_message || "Unable to load payment report.",
            { position: toast.POSITION.TOP_RIGHT }
          );
        }
        setReportLoading(false);
      })
      .catch(() => {
        setReportLoading(false);
        toast.error("Network error loading payment report.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const openPrint = () => {
    if (!selectedStudent?.Student_ID) return;
    const q = new URLSearchParams({
      Student_ID: String(selectedStudent.Student_ID),
      StartDate: dateFrom,
      EndDate: dateTo,
    });
    window.open(
      `/students/StudentPaymentReportPrint?${q.toString()}`,
      "_blank"
    );
  };

  const totalAmount =
    reportMeta?.TotalPaid ??
    payments.reduce((s, p) => s + Number(p.Amount || 0), 0);

  return (
    <>
      <StudentMenu />
      <RightsCheck />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/Dashboard">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/students/SearchStudents">Students</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Payment report
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Student payment report</div>
                <p className="text-muted small mb-0">
                  Payments before April 2026 come from the old student ledger
                  (credit entries). From April 2026 onward, paid challans from
                  tblChalanHead are included.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="card mb-3">
              <div className="card-body p-md-4">
                <Form noValidate validated={validated} onSubmit={loadReport}>
                  <div className="row gy-3 align-items-end">
                    <div className="col-md-4">
                      <label className="form-label">Registration no</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={searchRegNo}
                          onChange={(e) => setSearchRegNo(e.target.value)}
                          placeholder="e.g. 018685"
                        />
                        <button
                          type="button"
                          className="button button-white"
                          onClick={searchStudent}
                          disabled={searchLoading}
                        >
                          {searchLoading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            "Find"
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">From date</label>
                      <input
                        type="date"
                        className="form-control"
                        required
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">To date</label>
                      <input
                        type="date"
                        className="form-control"
                        required
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                      />
                    </div>
                  </div>

                  {selectedStudent && (
                    <div className="alert alert-light border mt-3 mb-0">
                      <strong>{selectedStudent.Student_Name}</strong>
                      <span className="text-muted">
                        {" "}
                        — Reg {selectedStudent.Registration_No}
                        {selectedStudent.SelectedClass?.Class_Name
                          ? `, ${selectedStudent.SelectedClass.Class_Name}`
                          : ""}
                        {selectedStudent.SelectedSection?.Section_Name
                          ? ` / ${selectedStudent.SelectedSection.Section_Name}`
                          : ""}
                      </span>
                    </div>
                  )}

                  <div className="mt-3 d-flex flex-wrap gap-2">
                    <button
                      type="submit"
                      className="button button-primary"
                      disabled={reportLoading || !selectedStudent}
                      onClick={() => scrollToTop()}
                    >
                      {reportLoading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading…
                        </>
                      ) : (
                        "Generate report"
                      )}
                    </button>
                    <button
                      type="button"
                      className="button button-white"
                      disabled={!selectedStudent || payments.length === 0}
                      onClick={openPrint}
                    >
                      <i className="ri-printer-line" /> Print
                    </button>
                  </div>
                </Form>
              </div>
            </div>

            {reportMeta && (
              <div className="card">
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-8">
                      <p className="mb-1">
                        <strong>Period:</strong>{" "}
                        {moment(reportMeta.DateFrom).format("DD MMM YYYY")} —{" "}
                        {moment(reportMeta.DateTo).format("DD MMM YYYY")}
                      </p>
                      <p className="mb-0 small text-muted">
                        Legacy ledger payments: {reportMeta.LegacyPaymentCount}{" "}
                        | Paid challans (from Apr 2026):{" "}
                        {reportMeta.ChallanPaymentCount}
                      </p>
                    </div>
                    <div className="col-md-4 text-md-end">
                      <h5 className="mb-0">
                        Total paid: Rs.{" "}
                        {Number(totalAmount).toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}
                      </h5>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-theme table-sm">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Date</th>
                          <th>Source</th>
                          <th>Receipt / Challan</th>
                          <th>Description</th>
                          <th className="text-end">Amount (Rs.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted">
                              No payments found in this date range.
                            </td>
                          </tr>
                        ) : (
                          payments.map((row, idx) => (
                            <tr key={`${row.Source}-${row.Reference_No}-${idx}`}>
                              <td>{idx + 1}</td>
                              <td className="text-nowrap">
                                {moment(row.Payment_Date).format("DD/MM/YYYY")}
                              </td>
                              <td>{row.Source}</td>
                              <td>{row.Reference_No || "—"}</td>
                              <td>
                                {row.Description}
                                {row.Fee_Code ? (
                                  <span className="text-muted small">
                                    {" "}
                                    ({row.Fee_Code})
                                  </span>
                                ) : null}
                                {row.Bank_Scroll_No ? (
                                  <span className="text-muted small d-block">
                                    Scroll: {row.Bank_Scroll_No}
                                  </span>
                                ) : null}
                              </td>
                              <td className="text-end text-nowrap">
                                {Number(row.Amount).toLocaleString(undefined, {
                                  maximumFractionDigits: 0,
                                })}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      {payments.length > 0 && (
                        <tfoot>
                          <tr>
                            <th colSpan={5} className="text-end">
                              Total
                            </th>
                            <th className="text-end">
                              {Number(totalAmount).toLocaleString(undefined, {
                                maximumFractionDigits: 0,
                              })}
                            </th>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentPaymentReport;
